// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import RequestRmaButtons, {
    RequestRmaButtonsStyles,
} from "@insite/content-library/Widgets/RequestRma/RequestRmaButtons";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { Link } from "@insite/mobius/index";
import { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import React, { FC, useContext } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    pageTitle: getCurrentPage(state).fields.title,
    // XNG-Changes
    orderDetailsUrl: getPageLinkByPageType(state, "OrderDetailsPage")?.url,
});

export interface RequestRmaHeaderStyles {
    headerGridContainer?: GridContainerProps;
    title: TypographyProps;
    titleGridItem: GridItemProps;
    buttonsGridItem?: GridItemProps;
    requestRmaButtons?: RequestRmaButtonsStyles;
    // XNG-Changes
    goBackLink?: LinkProps;
}

export const headerStyles: RequestRmaHeaderStyles = {
    buttonsGridItem: {
        css: css`
            justify-content: flex-end;
        `,
        width: [1, 1, 1, 6, 6],
    },
    titleGridItem: {
        width: [11, 11, 11, 6, 6],
    },
    title: {
        variant: "h2",
        as: "h1",
    },
};

const styles = headerStyles;

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

const RequestRmaHeader: FC<Props> = ({ pageTitle, orderDetailsUrl }) => {
    // XNG-Changes: FRD - start
    const { value: order } = useContext(OrderStateContext);
    const orderNumber = order?.erpOrderNumber || order?.webOrderNumber;
    // end
    return (
        <GridContainer {...styles.headerGridContainer}>
            <GridItem {...styles.titleGridItem}>
                <Typography {...styles.title}>{pageTitle}</Typography>
                {orderNumber && orderDetailsUrl && (
                    <Link href={`${orderDetailsUrl}?orderNumber=${orderNumber}`} {...styles.goBackLink}>
                        {translate("Return to Order Details")}
                    </Link>
                )}
            </GridItem>
            {/* XNG-Changes */}
            <GridItem {...styles.buttonsGridItem}>
                <RequestRmaButtons extendedStyles={styles.requestRmaButtons} />
            </GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, null)(RequestRmaHeader),
    definition: {
        displayName: "Page Header",
        allowedContexts: ["RequestRmaPage"],
        fieldDefinitions: [],
        group: "Return Request (RMA)",
    },
};

export default widgetModule;
